import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BirthdayService } from './birthday.service';
import { DatesNeeded, datesNeeded, isSnoozed } from '../shared/celebration-dates/celebration-dates.rules';

const SNOOZE_KEY = (uid: string) => `sb_dates_prompt_snooze_${uid}`;

export interface DatesValues {
  day: number | null;
  month: number | null;
  year: number | null;
  /** YYYY-MM-DD o vacío. */
  hireDate: string;
}

export interface DatesSaveResult {
  birthdayError: string | null;
  hireError: string | null;
}

/**
 * Pide a las cuentas de empresa los datos que faltan para celebrarlas (cumpleaños y aniversario) y los guarda con los
 * endpoints de siempre (`/birthdays/me/*`): la persona solo puede cambiar lo que RR.HH. no administra.
 */
@Injectable({ providedIn: 'root' })
export class CelebrationDatesService {
  private readonly birthdays = inject(BirthdayService);

  /** Qué falta pedirle a esta persona ahora; null si nada (o si pidió «Más tarde» hoy, o no es de empresa). */
  async evaluate(uid: string, hasCompany: boolean): Promise<DatesNeeded | null> {
    if (!hasCompany || isSnoozed(this.readSnooze(uid))) return null;
    try {
      const cfg = await firstValueFrom(this.birthdays.config());
      if (!cfg.enabled) return null;
      const [birth, hire] = await Promise.all([
        firstValueFrom(this.birthdays.getSettings()),
        cfg.anniversariesEnabled ? firstValueFrom(this.birthdays.getHire()) : Promise.resolve(null)
      ]);
      const needed = datesNeeded({ cfg, birth, hire });
      return needed.birthday || needed.hire ? needed : null;
    } catch {
      // sin red o sesión vencida: no se interrumpe a nadie por esto
      return null;
    }
  }

  /** «Más tarde»: no se vuelve a preguntar hasta mañana. */
  snooze(uid: string): void {
    try {
      localStorage.setItem(SNOOZE_KEY(uid), String(Date.now()));
    } catch {
      /* sin almacenamiento: se volverá a preguntar, no es grave */
    }
  }

  /** Guarda lo que la persona completó (los vacíos no se envían, así nunca pisan lo que ya hay). */
  async save(values: DatesValues, which: DatesNeeded = { birthday: true, hire: true }): Promise<DatesSaveResult> {
    const result: DatesSaveResult = { birthdayError: null, hireError: null };
    if (which.birthday && values.day !== null && values.month !== null) {
      try {
        await firstValueFrom(this.birthdays.saveSettings({ day: values.day, month: values.month, year: values.year }));
      } catch (err) {
        result.birthdayError = this.message(err, 'No pudimos guardar tu cumpleaños.');
      }
    }
    if (which.hire && values.hireDate) {
      try {
        await firstValueFrom(this.birthdays.saveHire({ hireDate: values.hireDate }));
      } catch (err) {
        result.hireError = this.message(err, 'No pudimos guardar tu aniversario.');
      }
    }
    return result;
  }

  private message(err: unknown, fallback: string): string {
    const e = err as { error?: { message?: string }; message?: string } | null;
    return e?.error?.message || fallback;
  }

  private readSnooze(uid: string): string | null {
    try {
      return localStorage.getItem(SNOOZE_KEY(uid));
    } catch {
      return null;
    }
  }
}
